import React, { type FocusEvent, type ChangeEvent, useState } from 'react';
import { Input as AntInput } from 'antd';
import type { InputProps as AntInputProps } from 'antd';
import { useField } from 'formik';
import classNames from 'classnames';
import numeral from 'numeral';

import { toEnglishDigits } from '@/common/utils/to-english-digits.utility';
import Text from '@/common/components/text/text.component';
import { regex } from '@/common/constants/regex.constant';

import SpinnerIcon from '~/public/assets/images/svg/loader-spinner-icon.svg';

interface FormikNumericInputProps extends AntInputProps {
  label?: string;
  name: string;
  id: string;
  icon?: string;
  fixFocused?: boolean;
  loading?: boolean;
  error?: string;
}

export const FormikNumericInput = ({
  id,
  label,
  className,
  onBlur,
  onChange,
  onFocus,
  icon,
  fixFocused,
  style,
  error,
  loading,
  ...rest
}: FormikNumericInputProps) => {
  const [field, , helpers] = useField(id);
  const [isFocused, seIsFocused] = useState<boolean>(fixFocused || false);
  const [handleErrorDisplay, sethandleErrorDisplay] = useState<boolean>(false);
  const { type } = rest;

  const onBlurHandler = (
    formikOnBlur: (e: ChangeEvent<HTMLInputElement>) => void,
    e: FocusEvent<HTMLInputElement>
  ): void => {
    formikOnBlur(e);
    if (e.target.value.length < 1 && !fixFocused) seIsFocused(false);

    if (onBlur) {
      onBlur(e);
    }
    sethandleErrorDisplay(false);
  };
  const onChangeHandler = (
    formikOnChange: (e: ChangeEvent<HTMLInputElement>) => void,
    e: ChangeEvent<HTMLInputElement>
  ): void => {
    const { setValue } = helpers;
    const { value } = e.target;
    let newValue = toEnglishDigits(value);
    formikOnChange(e);
    newValue = newValue.replace(regex.nonDigitCharacterRegex, '');
    if (type === 'amount') {
      newValue = numeral(newValue).format('0,0');
    }
    setValue(newValue);
    if (onChange) {
      onChange(e);
    }
  };
  const onFocusHandler = (e: FocusEvent<HTMLInputElement>) => {
    if (!fixFocused) seIsFocused(true);
    if (onFocus) {
      onFocus(e);
    }
    sethandleErrorDisplay(true);
  };

  return (
    <div className="relative my-1 mt-3 h-[84px]">
      <div className="h-[20px]"></div>
      <div className="relative">
        <AntInput
          rootClassName="formik-input"
          {...field}
          {...rest}
          id={id}
          type={type === 'amount' ? 'tel' : type}
          status={error ? 'error' : undefined}
          className={classNames(
            ' h-11 rounded-md text-xs font-medium text-black-default placeholder:text-xs placeholder:font-normal placeholder:text-black-200',
            className
          )}
          onFocus={(e) => {
            onFocusHandler(e);
          }}
          onBlur={(e) => {
            onBlurHandler(field.onBlur, e);
          }}
          onChange={(e) => {
            onChangeHandler(field.onChange, e);
          }}
          prefix={
            loading ? (
              <SpinnerIcon className="absolute left-4 z-20 h-5 w-5 animate-spin text-secondary-400" />
            ) : null
          }
        />
        <label
          htmlFor={id}
          className={classNames(
            'absolute z-10 block h-4 cursor-text text-2xs font-normal text-black-200 transition-all duration-300',
            {
              'right-[50%] top-[50%] translate-x-[50%] translate-y-[-50%] text-xs':
                !isFocused,
            },
            {
              '!-top-5 !right-0 !translate-x-0 !translate-y-0 !text-2xs':
                isFocused || field.value,
            }
          )}
        >
          {label}
        </label>
      </div>
      <Text className="mt-1 h-4 text-2xs font-medium text-red-default">
        {error && handleErrorDisplay
          ? ''
          : typeof error === 'string'
            ? error
            : ''}
      </Text>
    </div>
  );
};
